/**
 * Project card for grid view
 * Story 226: Projects Dashboard
 * Story 464: Added onExport prop and ProjectActions hover menu
 */

import type { ProjectListItem } from '../../hooks/useProjects';
import { StatusDot } from '../workspace/StatusDot';
import { ProjectActions } from './ProjectActions';
import './ProjectCard.css';

interface ProjectCardProps {
  project: ProjectListItem;
  onClick: () => void;
  onExport?: (slug: string) => void;
}

/** Format relative time (e.g., "2 hours ago") */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function ProjectCard({ project, onClick, onExport }: ProjectCardProps) {
  const { stats, recent_users } = project;

  return (
    <button className="project-card" onClick={onClick} type="button">
      {onExport && <ProjectActions projectSlug={project.slug} onExport={onExport} />}
      <div className="project-card__header">
        <h3 className="project-card__name">{project.name}</h3>
        <span className="project-card__slug">{project.slug}</span>
      </div>

      <div className="project-card__stats">
        <div className="project-card__stat">
          <span className="project-card__stat-value">{stats.story_count}</span>
          <span className="project-card__stat-label">stories</span>
        </div>
        <div className="project-card__stat project-card__stat--in-progress">
          <StatusDot status="in-progress" />
          <span className="project-card__stat-value">{stats.in_progress_count}</span>
          <span className="project-card__stat-label">in progress</span>
        </div>
        <div className="project-card__stat project-card__stat--done">
          <StatusDot status="done" />
          <span className="project-card__stat-value">{stats.done_count}</span>
          <span className="project-card__stat-label">done</span>
        </div>
      </div>

      <div className="project-card__footer">
        <div className="project-card__users">
          {recent_users.slice(0, 3).map((user, i) => (
            <div
              key={user.github_username || i}
              className="project-card__avatar"
              title={user.github_username || "Unknown"}
            >
              {user.github_username ? (
                <img
                  src={`https://github.com/${user.github_username}.png?size=32`}
                  alt={user.github_username}
                  className="project-card__avatar-img"
                />
              ) : (
                <span className="project-card__avatar-initials">??</span>
              )}
            </div>
          ))}
          {stats.user_count > 3 && (
            <div className="project-card__avatar project-card__avatar--overflow">
              +{stats.user_count - 3}
            </div>
          )}
        </div>
        <span className="project-card__updated">
          {formatRelativeTime(project.updated_at)}
        </span>
      </div>
    </button>
  );
}
